import {useAtomValue, useStore} from 'jotai';
import {useCallback} from 'react';
import {frameSelectionAtom} from './atoms';
import type {FrameSelection} from './atoms';
import {getSelectionContext} from './selectionContext';
import {getCssSelector} from 'css-selector-generator';

interface UseFrameSelectionResult {
  /** The `kind` discriminant of the current selection — reactive. */
  selectedFrameType: FrameSelection['kind'];
  /** The `screenId` of the selected/editing element, or `null` — reactive. */
  selectedScreenId: string | null;
  /** The selected/editing `HTMLElement`, or `null` — reactive. */
  selectedElement: HTMLElement | null;
  /** CSS selector of selected element */
  selectedElementSelector: string | null;
  /** Banani Component Path of Selected Element */
  selectedElementContext: string | null;

  /** Imperatively read the full current state (safe inside callbacks). */
  getFrameSelection: () => FrameSelection;
  /** Transition to any selection state. */
  setFrameSelection: (next: FrameSelection) => void;
  /** Convenience shorthand: transition to `{kind: 'none'}`. */
  clearFrameSelection: () => void;
}

/**
 * Single access point for the frame-selection state machine.
 *
 * Reactive fields (`selectedFrameType`, `selectedScreenId`,
 * `selectedElement`) re-render the component when the atom changes.
 * `getFrameSelection()` reads the atom imperatively inside callbacks without
 * causing extra renders.
 */
export function useFrameSelection(): UseFrameSelectionResult {
  const store = useStore();
  const state = useAtomValue(frameSelectionAtom);

  const getFrameSelection = useCallback(
    () => store.get(frameSelectionAtom),
    [store],
  );

  const setFrameSelection = useCallback(
    (next: FrameSelection) => store.set(frameSelectionAtom, next),
    [store],
  );

  const clearFrameSelection = useCallback(
    () => store.set(frameSelectionAtom, {kind: 'none'}),
    [store],
  );

  return {
    selectedFrameType: state.kind,
    selectedScreenId: state.kind === 'none' ? null : state.screenId,
    selectedElement: state.kind === 'none' ? null : state.element,
    selectedElementContext:
      state.kind === 'none' ? null : state.selectionContext,
    selectedElementSelector:
      state.kind === 'none' ? null : state.elementSelector,

    getFrameSelection,
    setFrameSelection,
    clearFrameSelection,
  };
}

/**
 * Setter-only counterpart to {@link useFrameSelection} for callers that just
 * need to clear the selection. Avoids subscribing to `frameSelectionAtom`,
 * which would re-render the caller on every selection change.
 */
export function useClearFrameSelection() {
  const store = useStore();
  return useCallback(
    () => store.set(frameSelectionAtom, {kind: 'none'}),
    [store],
  );
}

/**
 * Imperative-only counterpart to {@link useFrameSelection} for callers that
 * never need to react to selection changes. Avoids subscribing to
 * `frameSelectionAtom`, so a selection change in any frame doesn't re-render
 * every consumer.
 */
export function useFrameSelectionActions() {
  const store = useStore();

  const getFrameSelection = useCallback(
    () => store.get(frameSelectionAtom),
    [store],
  );

  const setActiveSelection = useCallback(
    (next: {
      kind: 'element' | 'editing-text';
      element: HTMLElement;
      screenId: string;
    }) => {
      store.set(frameSelectionAtom, {
        ...next,
        selectionContext: getSelectionContext(next.element),
        elementSelector: getCssSelector(next.element, {
          blacklist: ['[data-idx]', '[data-file]'],
          includeTag: true,
          root: next.element.closest('[data-banani-screenid]'),
        }),
      });
    },
    [store],
  );

  const clearFrameSelection = useCallback(
    () => store.set(frameSelectionAtom, {kind: 'none'}),
    [store],
  );

  return {getFrameSelection, setActiveSelection, clearFrameSelection};
}
